import type { MatchPlayerView, MatchView } from '@mogzo/shared';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import type { UiTokens } from './look';

/** How often a waiting panel asks how its match is doing (MULTIPLAYER_LIMITS.pollMs). */
const POLL_MS = 2000;

/** Where the multiplayer panels find, follow and leave matches (the play API, or mogzo dev's). */
export interface MultiplayerData {
  find(mode: string): Promise<MatchView>;
  create(mode: string): Promise<MatchView>;
  join(code: string): Promise<MatchView>;
  poll(id: string): Promise<MatchView>;
  start(id: string): Promise<MatchView>;
  leave(id: string): Promise<void>;
  rematch(id: string): Promise<MatchView>;
}

/** What opens the multiplayer panel. */
export type MatchOpen =
  | { how: 'quick'; mode: string }
  | { how: 'invite'; mode: string }
  | { how: 'join'; code?: string }
  | { how: 'results'; match: MatchView };

/** What the player did, for the game: its match started, or they gave up, or chose after results. */
export type MatchOutcome =
  | { action: 'start' | 'rematch' | 'new'; match: MatchView }
  | { action: 'cancel' }
  | { action: 'done' };

type Step =
  | { kind: 'loading'; label: string }
  | { kind: 'code' }
  | { kind: 'match'; match: MatchView; after: 'start' | 'rematch' | 'new' }
  | { kind: 'results'; match: MatchView }
  | { kind: 'failed'; message: string; retry: (() => void) | null };

const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
};

function Svg({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const initials = (name: string) =>
  name
    .split(/[\s_.-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

/** A player's round badge; the player's own is in the accent colour. */
function Avatar({ t, player, size = 32 }: { t: UiTokens; player: MatchPlayerView; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: 'none',
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        background: player.me ? t.colours.accent : t.colours.line,
        color: player.me ? t.colours.onAccent : t.colours.text,
        fontSize: size / 2.7,
        fontWeight: 700,
      }}
    >
      {initials(player.name)}
    </span>
  );
}

/** A spinning ring, unless the player asked for reduced motion (no <style> on mogzo.com). */
function Spinner({ t }: { t: UiTokens }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const ring = ref.current;
    if (!ring?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const spin = ring.animate([{ rotate: '0deg' }, { rotate: '360deg' }], {
      duration: 1100,
      iterations: Number.POSITIVE_INFINITY,
    });
    return () => spin.cancel();
  }, []);
  return (
    <svg ref={ref} width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
      <circle cx="28" cy="28" r="25" fill="none" stroke={t.colours.line} strokeWidth="3" />
      <path
        d="M28 3a25 25 0 0 1 25 25"
        fill="none"
        stroke={t.colours.accent}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Seconds since a match was made (as the last check said), counting up between checks. */
function useWaited(waited: number) {
  const [now, setNow] = useState(() => Date.now());
  const [from, setFrom] = useState(() => ({ at: Date.now(), waited }));
  useEffect(() => setFrom({ at: Date.now(), waited }), [waited]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return from.waited + Math.max(0, Math.floor((now - from.at) / 1000));
}

function ordinal(n: number): string {
  const tens = n % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? 'th'
      : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th');
  return `${n}${suffix}`;
}

/** Copies text, and says so for a moment. */
function CopyButton({
  t,
  text,
  label,
  primary,
}: {
  t: UiTokens;
  text: string;
  label: string;
  primary?: boolean;
}) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      style={{ ...(primary ? t.primary : t.secondary), flex: 1 }}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? 'Copied' : label}
    </button>
  );
}

/** The players in a waiting match, with a place for each still to come. */
function PlayerList({ t, match }: { t: UiTokens; match: MatchView }) {
  const open = Math.max(0, match.mode.maxPlayers - match.players.length);
  const row: CSSProperties = {
    minHeight: 46,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '0 12px',
    borderRadius: t.innerRadius,
  };
  return (
    <ul
      aria-label="Players"
      style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}
    >
      {match.players.map((p) => (
        <li key={p.playerId} style={{ ...row, background: t.colours.field }}>
          <Avatar t={t} player={p} />
          <span style={{ flex: 1, minWidth: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>
            {p.name}
            {p.me && <span style={{ fontWeight: 400, color: t.colours.muted }}> (you)</span>}
          </span>
          {p.host && <span style={{ fontSize: '0.87em', color: t.colours.muted }}>Host</span>}
        </li>
      ))}
      {open > 0 && (
        <li style={{ ...row, border: `1px dashed ${t.colours.line}`, color: t.colours.muted }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              boxSizing: 'border-box',
              borderRadius: '50%',
              border: `2px dashed ${t.colours.line}`,
            }}
          />
          {open === 1 ? '1 open place' : `${open} open places`}
        </li>
      )}
    </ul>
  );
}

/**
 * MOGZO's multiplayer panel (M31): finding a player by quick match, an invite code to share
 * or type in, waiting for players, and the results with a rematch. It finishes with what the
 * player did; the game hears about it only once its match starts (or they give up).
 */
export function MatchPanel({
  t,
  data,
  open,
  phone,
  onDone,
}: {
  t: UiTokens;
  data: MultiplayerData | undefined;
  open: MatchOpen;
  phone: boolean;
  onDone: (outcome: MatchOutcome) => void;
}) {
  const [step, setStep] = useState<Step>(() =>
    open.how === 'results'
      ? { kind: 'results', match: open.match }
      : open.how === 'join' && !open.code
        ? { kind: 'code' }
        : { kind: 'loading', label: open.how === 'quick' ? 'Finding a player' : 'Getting a code' },
  );
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const done = useRef(false);
  const finish = (outcome: MatchOutcome) => {
    if (done.current) return;
    done.current = true;
    onDone(outcome);
  };
  const reason = (error: unknown) =>
    error instanceof Error && error.message
      ? error.message
      : 'MOGZO couldn’t answer just now. Try again.';

  /** Runs a call that leads to a match to follow. */
  const follow = (load: () => Promise<MatchView>, after: 'start' | 'rematch' | 'new') => {
    if (!data) {
      setStep({ kind: 'failed', message: 'Multiplayer isn’t available here.', retry: null });
      return;
    }
    setBusy(true);
    setProblem(null);
    load()
      .then((match) => setStep({ kind: 'match', match, after }))
      .catch((error) =>
        setStep({ kind: 'failed', message: reason(error), retry: () => follow(load, after) }),
      )
      .finally(() => setBusy(false));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once, for how the panel opened
  useEffect(() => {
    if (!data) return;
    if (open.how === 'quick') follow(() => data.find(open.mode), 'start');
    if (open.how === 'invite') follow(() => data.create(open.mode), 'start');
    if (open.how === 'join' && open.code) follow(() => data.join(open.code ?? ''), 'start');
  }, []);

  // A match being followed is checked every couple of seconds until it starts or ends.
  const following = step.kind === 'match' || step.kind === 'results' ? step.match : null;
  const waited = useWaited(following?.waited ?? 0);
  /** Bumped when a check fails, so the next one still happens. */
  const [retries, setRetries] = useState(0);
  const followingKey = following ? `${following.id}:${following.status}:${following.results}` : '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: restarts when the match changes state
  useEffect(() => {
    if (!data || !following) return;
    if (following.status === 'cancelled') return;
    if (step.kind === 'match' && following.status === 'playing') {
      finish({ action: step.after, match: following });
      return;
    }
    // Results keep being checked: for the last results, and for someone asking for a rematch.
    let active = true;
    const timer = setTimeout(() => {
      data
        .poll(following.id)
        .then((match) => {
          if (!active) return;
          setStep((current) =>
            current.kind === 'match' || current.kind === 'results'
              ? { ...current, match }
              : current,
          );
        })
        .catch(() => active && setRetries((n) => n + 1));
    }, POLL_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [data, followingKey, step, retries]);

  const leave = (match: MatchView | null) => {
    if (match && data && match.status === 'waiting')
      void data.leave(match.id).catch(() => undefined);
    finish({ action: 'cancel' });
  };

  const heading = (title: string, sub?: ReactNode) => (
    <div>
      <h2 id="mogzo-panel-title" style={{ margin: 0, fontSize: '1.3em', fontWeight: 700 }}>
        {title}
      </h2>
      {sub && (
        <p style={{ margin: '2px 0 0', fontSize: '0.93em', color: t.colours.muted }}>{sub}</p>
      )}
    </div>
  );
  const centred: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: t.gap + 4,
    textAlign: 'center',
  };
  const wide: CSSProperties = { width: '100%', boxSizing: 'border-box' };

  if (step.kind === 'loading') {
    return (
      <div style={centred}>
        <Spinner t={t} />
        {heading(step.label)}
        <button
          type="button"
          style={{ ...t.secondary, ...wide }}
          onClick={() => leave(null)}
          data-autofocus
        >
          Cancel
        </button>
      </div>
    );
  }

  if (step.kind === 'failed') {
    return (
      <div style={centred}>
        {heading('Couldn’t start multiplayer')}
        <p role="alert" style={{ margin: 0, color: t.colours.muted, maxWidth: '34ch' }}>
          {step.message}
        </p>
        {step.retry && (
          <button
            type="button"
            style={{ ...t.primary, ...wide }}
            onClick={step.retry}
            disabled={busy}
            data-autofocus
          >
            Try again
          </button>
        )}
        <button type="button" style={{ ...t.secondary, ...wide }} onClick={() => leave(null)}>
          Close
        </button>
      </div>
    );
  }

  if (step.kind === 'code') {
    const ready = code.replace(/[\s-]/g, '').length === 6;
    return (
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: t.gap + 4 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (!data || !ready) return;
          setBusy(true);
          setProblem(null);
          data
            .join(code)
            .then((match) => setStep({ kind: 'match', match, after: 'start' }))
            .catch((error) => setProblem(reason(error)))
            .finally(() => setBusy(false));
        }}
      >
        {heading('Join a friend', 'Type the 6-letter code they sent you.')}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: '0.87em',
            fontWeight: 600,
            color: t.colours.muted,
          }}
        >
          Invite code
          <input
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 9))}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            data-autofocus
            style={{
              boxSizing: 'border-box',
              height: 60,
              padding: '0 16px',
              border: `2px solid ${t.colours.accent}`,
              borderRadius: t.innerRadius,
              background: t.colours.field,
              color: t.colours.text,
              font: 'inherit',
              fontSize: 28,
              fontWeight: 600,
              letterSpacing: '0.35em',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          />
        </label>
        {problem && (
          <p role="alert" style={{ margin: 0, color: t.colours.text }}>
            {problem}
          </p>
        )}
        <button
          type="submit"
          style={{ ...t.primary, opacity: ready && !busy ? 1 : 0.5 }}
          disabled={!ready || busy}
        >
          {busy ? 'Joining…' : 'Join'}
        </button>
        <button type="button" style={t.secondary} onClick={() => leave(null)}>
          Cancel
        </button>
      </form>
    );
  }

  return step.kind === 'results' ? results(step.match) : followed(step.match, step.after);

  function followed(match: MatchView, after: 'start' | 'rematch' | 'new') {
    const me = match.players.find((p) => p.me);
    const count = match.players.length;
    if (match.status === 'cancelled') {
      return (
        <div style={centred}>
          {heading('This match was called off', 'Everyone else left, or the code ran out.')}
          <button
            type="button"
            style={{ ...t.primary, ...wide }}
            onClick={() => finish({ action: 'cancel' })}
            data-autofocus
          >
            Close
          </button>
        </div>
      );
    }
    // Quick match before anyone else is in: searching.
    if (match.how === 'quick' && count < 2) {
      return (
        <div style={centred}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {me && <Avatar t={t} player={me} size={56} />}
            <span style={{ fontSize: 13, fontWeight: 700, color: t.colours.muted }}>VS</span>
            <span style={{ position: 'relative', width: 56, height: 56 }}>
              <Spinner t={t} />
            </span>
          </div>
          {heading(
            'Finding a player',
            <>
              {match.mode.name} ·{' '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{clock(waited)}</span>
            </>,
          )}
          <p role="status" style={{ margin: 0, maxWidth: '30ch', color: t.colours.muted }}>
            Looking for someone near your skill. It usually takes under a minute.
          </p>
          <button
            type="button"
            style={{ ...t.secondary, ...wide }}
            onClick={() => leave(match)}
            data-autofocus
          >
            Cancel
          </button>
        </div>
      );
    }
    const host = me?.host ?? false;
    const canStart = host && count >= match.mode.minPlayers && count < match.mode.maxPlayers;
    const sub = (
      <>
        {match.mode.name} · {count} of {match.mode.maxPlayers}
        {match.code && (
          <>
            {' '}
            · code <span style={{ color: t.colours.text, fontWeight: 600 }}>{match.code}</span>
          </>
        )}
      </>
    );
    const inviting = match.how === 'invite' && match.code && count < 2 && after === 'start';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: t.gap + 4 }}>
        {inviting ? (
          <>
            {heading('Play a friend', `${match.mode.name} · send them the code or the link`)}
            {/* Read out letter by letter, so a screen reader spells the code. */}
            <p style={visuallyHidden}>Invite code {match.code?.split('').join(' ')}</p>
            <div
              aria-hidden="true"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 8 }}
            >
              {match.code?.split('').map((letter, index) => (
                <span
                  // biome-ignore lint/suspicious/noArrayIndexKey: a code's letters repeat
                  key={index}
                  style={{
                    height: phone ? 52 : 60,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: t.innerRadius,
                    background: t.colours.field,
                    fontSize: phone ? 26 : 30,
                    fontWeight: 600,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  {letter}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {phone && typeof navigator.share === 'function' && match.inviteUrl ? (
                <button
                  type="button"
                  style={{ ...t.primary, flex: 1 }}
                  onClick={() =>
                    void navigator
                      .share({ title: `Play ${match.mode.name}`, url: match.inviteUrl ?? '' })
                      .catch(() => undefined)
                  }
                  data-autofocus
                >
                  Share
                </button>
              ) : (
                match.inviteUrl && (
                  <CopyButton t={t} text={match.inviteUrl} label="Copy link" primary />
                )
              )}
              {match.code && <CopyButton t={t} text={match.code} label="Copy code" />}
            </div>
            {match.inviteUrl && (
              <p
                style={{
                  margin: 0,
                  padding: '8px 12px',
                  borderRadius: t.innerRadius,
                  background: t.colours.field,
                  color: t.colours.muted,
                  fontSize: '0.87em',
                  overflowWrap: 'anywhere',
                }}
              >
                {match.inviteUrl.replace(/^https?:\/\//, '')}
              </p>
            )}
            <p role="status" style={{ margin: 0, color: t.colours.muted, fontSize: '0.93em' }}>
              Waiting for your friend · the code works for 30 minutes
            </p>
          </>
        ) : (
          <>
            {heading(after === 'rematch' ? 'Rematch' : 'Waiting for players', sub)}
            <PlayerList t={t} match={match} />
            <p role="status" style={visuallyHidden}>
              {count} of {match.mode.maxPlayers} players
            </p>
          </>
        )}
        {canStart && (
          <button
            type="button"
            style={t.primary}
            disabled={busy}
            onClick={() => {
              if (!data) return;
              setBusy(true);
              data
                .start(match.id)
                .then((started) => setStep({ kind: 'match', match: started, after }))
                .catch((error) => setProblem(reason(error)))
                .finally(() => setBusy(false));
            }}
          >
            Start with {count} players
          </button>
        )}
        {problem && (
          <p role="alert" style={{ margin: 0 }}>
            {problem}
          </p>
        )}
        <button type="button" style={t.secondary} onClick={() => leave(match)}>
          {count > 1 ? 'Leave' : 'Cancel'}
        </button>
        {!inviting && count < match.mode.maxPlayers && (
          <p style={{ margin: 0, textAlign: 'center', fontSize: '0.87em', color: t.colours.muted }}>
            {match.how === 'quick'
              ? 'Starts when the match is full, or soon with enough players.'
              : match.mode.maxPlayers === 2
                ? 'Starts by itself when both places are filled.'
                : `Starts by itself when all ${match.mode.maxPlayers} places are filled.`}
          </p>
        )}
      </div>
    );
  }

  function results(match: MatchView) {
    const me = match.players.find((p) => p.me);
    const ordered = [...match.players].sort(
      (a, b) => (a.place ?? Number.POSITIVE_INFINITY) - (b.place ?? Number.POSITIVE_INFINITY),
    );
    const winners = match.players.filter((p) => p.place === 1);
    const draw = winners.length === match.players.length && match.players.length > 1;
    const title = !match.results
      ? 'Waiting for results'
      : draw
        ? 'It’s a draw'
        : me?.place === 1
          ? 'You won'
          : me?.place
            ? `You came ${ordinal(me.place)}`
            : 'Match over';
    const missing = match.players.filter((p) => p.score === null && p.place === null && !p.left);
    const others = match.rematch ? match.rematch.players : 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: t.gap + 4 }}>
        <div style={centred}>
          <span
            aria-hidden="true"
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: me?.place === 1 && !draw ? t.colours.accent : t.colours.field,
              color: me?.place === 1 && !draw ? t.colours.onAccent : t.colours.accent,
              fontWeight: 700,
              fontSize: 20,
            }}
          >
            {me?.place === 1 && !draw ? (
              <Svg size={26}>
                <path d="M8 21h8" />
                <path d="M12 17v4" />
                <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
                <path d="M17 5h3v2a3 3 0 0 1-3 3" />
                <path d="M7 5H4v2a3 3 0 0 0 3 3" />
              </Svg>
            ) : me?.place ? (
              ordinal(me.place)
            ) : (
              <Spinner t={t} />
            )}
          </span>
          {heading(title, `${match.mode.name} · ${match.players.length} players`)}
        </div>
        <ol
          aria-label="Results"
          style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}
        >
          {ordered.map((p) => (
            <li
              key={p.playerId}
              style={{
                minHeight: 50,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '0 14px',
                borderRadius: t.innerRadius,
                background: t.colours.field,
                boxShadow: p.me ? `inset 0 0 0 2px ${t.colours.accent}` : undefined,
              }}
            >
              <b style={{ width: 18, textAlign: 'center' }}>{p.place ?? '–'}</b>
              <Avatar t={t} player={p} />
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>
                {p.name}
                {p.me && <span style={{ fontWeight: 400, color: t.colours.muted }}> (you)</span>}
                {p.left && <span style={{ fontWeight: 400, color: t.colours.muted }}> · left</span>}
              </span>
              {p.score !== null && (
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {p.score.toLocaleString('en')}
                </span>
              )}
            </li>
          ))}
        </ol>
        {!match.results && missing.length > 0 && (
          <p role="status" style={{ margin: 0, textAlign: 'center', color: t.colours.muted }}>
            Waiting for {missing.map((p) => p.name).join(', ')}
          </p>
        )}
        <button
          type="button"
          style={t.primary}
          disabled={busy}
          data-autofocus
          onClick={() => data && follow(() => data.rematch(match.id), 'rematch')}
        >
          {match.players.length > 2 ? 'Play again together' : 'Rematch'}
        </button>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <button
            type="button"
            style={t.secondary}
            disabled={busy}
            onClick={() => data && follow(() => data.find(match.mode.id), 'new')}
          >
            {match.mode.kind === 'realtime' ? 'New room' : 'New match'}
          </button>
          <button type="button" style={t.secondary} onClick={() => finish({ action: 'done' })}>
            Done
          </button>
        </div>
        {others > 0 && (
          <p
            role="status"
            style={{ margin: 0, textAlign: 'center', fontSize: '0.87em', color: t.colours.muted }}
          >
            {others === 1 ? 'Someone wants a rematch' : `${others} players want a rematch`}
          </p>
        )}
      </div>
    );
  }
}

/**
 * Realtime rooms (M35): "Reconnecting…" with the time the player's place is kept, "You left the
 * room" when it wasn't kept long enough, and "Multiplayer is busy" when no room could open.
 */
export function RoomPanel({
  t,
  request,
  onDone,
}: {
  t: UiTokens;
  request: { show: 'reconnecting'; until: number } | { show: 'lost' | 'busy' };
  onDone: (choice: { action: 'leave' | 'find' | 'done' }) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (request.show !== 'reconnecting') return;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [request.show]);
  const centred: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: t.gap + 4,
    textAlign: 'center',
  };
  const wide: CSSProperties = { width: '100%', boxSizing: 'border-box' };
  const title = (text: string) => (
    <h2 id="mogzo-panel-title" style={{ margin: 0, fontSize: '1.3em', fontWeight: 700 }}>
      {text}
    </h2>
  );
  const say = (text: ReactNode) => (
    <p style={{ margin: 0, color: t.colours.muted, maxWidth: '34ch', lineHeight: 1.45 }}>{text}</p>
  );

  if (request.show === 'reconnecting') {
    const left = Math.max(0, Math.ceil((request.until - now) / 1000));
    const share = Math.min(1, left / 30);
    return (
      <div style={centred}>
        <Spinner t={t} />
        {title('Reconnecting…')}
        {say(
          <>
            Your connection dropped. Your place in the room is kept for{' '}
            <strong style={{ color: t.colours.text, fontVariantNumeric: 'tabular-nums' }}>
              {clock(left)}
            </strong>
            .
          </>,
        )}
        <div
          role="progressbar"
          aria-label="Time your place is kept"
          aria-valuemin={0}
          aria-valuemax={30}
          aria-valuenow={left}
          style={{ ...wide, height: 6, borderRadius: 3, background: t.colours.line }}
        >
          <div
            style={{
              width: `${share * 100}%`,
              height: 6,
              borderRadius: 3,
              background: t.colours.accent,
            }}
          />
        </div>
        <button
          type="button"
          style={{ ...t.secondary, ...wide }}
          onClick={() => onDone({ action: 'leave' })}
          data-autofocus
        >
          Leave the room
        </button>
      </div>
    );
  }

  const lost = request.show === 'lost';
  return (
    <div style={centred}>
      {title(lost ? 'You left the room' : 'Multiplayer is busy')}
      {say(
        lost
          ? 'The connection didn’t come back within 30 seconds, so the room went on without you.'
          : 'Too many rooms are open on MOGZO right now. Try again later.',
      )}
      <button
        type="button"
        style={{ ...t.primary, ...wide }}
        onClick={() => onDone({ action: 'find' })}
        data-autofocus
      >
        {lost ? 'Find another room' : 'Try again'}
      </button>
      <button
        type="button"
        style={{ ...t.secondary, ...wide }}
        onClick={() => onDone({ action: 'done' })}
      >
        Done
      </button>
    </div>
  );
}
